import { z } from "zod";

/**
 * OS image form validation.
 *
 * Create and edit use the SAME schema: the backend PUT replaces the whole row, so a partial
 * update shape would let a save wipe fields the form left blank. `templateName` is therefore
 * editable on both paths — it is the string sent to WiService at provisioning time, and
 * getting it wrong is exactly the mistake this form exists to prevent.
 *
 * `osType` is deliberately absent: the backend derives it from metadata and the name.
 */
export const osImageFormSchema = z.object({
  name: z
    .string()
    .min(3, "Name must be at least 3 characters")
    .max(100, "Name must not exceed 100 characters"),
  version: z
    .string()
    .min(1, "Version is required")
    .max(50, "Version must not exceed 50 characters"),
  category: z.enum(["Linux", "Windows", "BSD", "Custom"]),
  description: z
    .string()
    .min(10, "Description must be at least 10 characters")
    .max(1000, "Description must not exceed 1000 characters"),
  // Absolute http(s) URL or a root-relative path served by the frontend apps
  // (e.g. /images/os/almalinux.svg). Protocol-relative "//host" is rejected.
  iconUrl: z
    .string()
    .optional()
    .refine(
      (value) =>
        !value ||
        (value.startsWith("/") && value.length > 1 && !value.startsWith("//")) ||
        z.string().url().safeParse(value).success,
      "Must be an http(s) URL or a root-relative path (e.g. /images/os/ubuntu.svg)",
    ),
  templateName: z
    .string()
    .min(1, "Template name is required")
    .max(100, "Template name must not exceed 100 characters"),
  minimumSpecs: z.object({
    cpu: z
      .number()
      .int("CPU must be an integer")
      .min(1, "CPU must be at least 1 core")
      .max(128, "CPU must not exceed 128 cores"),
    memory: z
      .number()
      .int("Memory must be an integer")
      .min(512, "Memory must be at least 512 MB")
      .max(1048576, "Memory must not exceed 1 TB"),
    disk: z
      .number()
      .int("Disk must be an integer")
      .min(10, "Disk must be at least 10 GB")
      .max(10240, "Disk must not exceed 10 TB"),
  }),
  isActive: z.boolean().default(true),
  isFeatured: z.boolean().default(false),
  // Sort Order removed from the admin (2026-09-11): the catalog is ordered by name. To restore,
  // re-add `sortOrder: z.number().int().min(0).default(0)` with the backend SortOrder fields.
  // Windows licence notice, stored in metadata.windowsLicense and shown in the customer VM wizard.
  // The message may be HTML (sanitised to an allowlist at render time), hence the larger limit.
  licenseActivationSupported: z.boolean().default(true),
  licenseWarningMessage: z
    .string()
    .max(4000, "Warning message must not exceed 4000 characters")
    .default(""),
}).superRefine((values, ctx) => {
  // A Windows image marked unsupported must carry a message: the customer wizard shows only what is
  // written here, so an empty one would leave the customer with no warning at all.
  if (
    values.category === "Windows" &&
    !values.licenseActivationSupported &&
    !values.licenseWarningMessage.trim()
  ) {
    ctx.addIssue({
      code: "custom",
      path: ["licenseWarningMessage"],
      message: "Write the warning customers will see when they choose this image",
    });
  }
});

export type OSImageFormData = z.infer<typeof osImageFormSchema>;
